import React, { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useIsQueryOwner } from '@/hooks'
import NextImage from 'next/image'
import { MarkerList, useMobile } from 'emt-components-v2'
import MapDesktop from '@/components/search/Map/MapDesktop'
import Box from '@mui/material/Box'
import VariantImages from '@/assets/VariantImages'
import MapMobile from '@/components/search/Map/MapMobile'
import { styled } from '@mui/material/styles'
import useCurrentUser from '@/hooks/useCurrentUser'
import { UserCard } from '@/dataV3/Search/UserCard'
import { SearchFiltersValues, SearchType } from '@/modules/Search/apiTypes/base'
import { searchProfilesAsync } from '@/modules/Search/SearchServiceHelper'
import useFloatingProfile from '@/hooks/useFloatingProfile'

type Props = {
  onClose?: () => void
  itemsPerPage: number
  floating?: ReturnType<typeof useFloatingProfile>
}

export type MapFinderCoordinates = { lng: number | undefined; lat: number | undefined }

export const MarkerContainer = styled(Box)(() => ({
  cursor: 'pointer',
  '&.mapboxgl-marker': {
    zIndex: 1,
    '&.permanent': {
      zIndex: '9 !important',
    },
    '&.active': {
      zIndex: '8 !important',
    },
  },
  '& img': { cursor: 'pointer' },
  '& .img-marker': {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    width: 33,
    height: 33,
    background: 'white',
    borderRadius: '100%',
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08), 0px 4px 24px rgba(0, 0, 0, 0.08)',
    transition: 'all 0.2s linear',
    '& img': { transition: 'all 0.2s linear', opacity: 0.6, width: 33, height: 33 },
    '&:hover': {
      '& img': { opacity: 1 },
    },
  },
  '&.active': {
    '& .img-marker': {
      '& img': { opacity: 1 },
    },
  },
  '&.permanent': {
    '& .img-marker': {
      width: 42,
      height: 42,
      boxShadow: '0px 4px 14px 2px rgba(0, 0, 0, 0.19), 0px 4px 24px rgba(0, 0, 0, 0.08)',
      '& img': { opacity: 1, width: 43, height: 43 },
    },
  },
}))

const Map: React.FC<Props> = (props) => {
  const { onClose, floating } = props
  const [isQueryOwner, type] = useIsQueryOwner()
  const { settings } = useCurrentUser()

  // Au montage, si une position a été sauvegardée dans localStorage, on la charge
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedPos = localStorage.getItem('lastMapPosition')
      if (savedPos) {
        setCoordinates(JSON.parse(savedPos))
      }
    }
  }, [])

  const [coordinates, setCoordinates] = useState<MapFinderCoordinates>({
    lng: settings?.address?.longitude,
    lat: settings?.address?.latitude,
  })
  // À chaque changement de position, on sauvegarde les coordonnées dans le localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('lastMapPosition', JSON.stringify(coordinates))
    }
  }, [coordinates])

  const [markers, setMarkers] = useState<MarkerList>([])
  const [selectedProfile, setSelectedProfile] = useState<UserCard>()
  const [selectedProfileId, setSelectedProfileId] = useState<number | string>()
  const [distance, setDistance] = useState<number>(window.screen.width <= 600 ? 0.1 : 0.2)
  const [isFirstCall, setIsFirstCall] = useState<boolean>(true)
  const [filters, setFilters] = useState<SearchFiltersValues>()
  const [isMobile] = useMobile()

  const homeMarker = (
    <Box sx={{ cursor: 'pointer' }}>
      <NextImage src={VariantImages.MapPinHome} quality={100} alt={''} />
    </Box>
  )

  const handleMarkerClick = (id: number | string) => {
    const wrapper = document.getElementById(`mapdrawer-cards-wrapper`) as HTMLElement
    const { offsetTop: cardOffsetTop = 0 } = document.getElementById(`emtcard-${id}`) || {}
    const MobileSelectedWrapper = document.getElementById(`mapdrawer-current-cards-wrapper`) as HTMLElement
    const { offsetLeft: selectedCardOffsetLeft = 0 } = document.getElementById(`emtcard-selected-${id}`) || {}
    const htmlMarkers = document.getElementsByClassName('marker-container')

    Array.prototype.forEach.call(htmlMarkers, (marker) => {
      if (marker.id === id) {
        marker.classList.add('permanent')
        marker.classList.add('active')
      } else {
        marker.classList.remove('active')
        marker.classList.remove('permanent')
      }
    })

    const drawerPaperSelector = wrapper?.querySelector('.MuiDrawer-paper')

    const scrollYToPosition =
      isMobile || !drawerPaperSelector
        ? cardOffsetTop - wrapper?.clientHeight / 3
        : cardOffsetTop - drawerPaperSelector.clientHeight / 3

    const scrollXToPosition =
      isMobile || !drawerPaperSelector ? selectedCardOffsetLeft - (MobileSelectedWrapper.clientWidth - 320) / 2 : 0

    if (isMobile || !drawerPaperSelector) {
      // Mobile
      wrapper.scrollTo({ top: scrollYToPosition, left: 0, behavior: 'smooth' })
      MobileSelectedWrapper.scrollTo({ top: 0, left: scrollXToPosition, behavior: 'smooth' })
    } else {
      // Desktop
      drawerPaperSelector.scrollTo({ top: scrollYToPosition, left: 0, behavior: 'smooth' })
    }
    setSelectedProfileId(id)
    setSelectedProfile(dataProfiles.filter((profile) => profile.id === id)[0])
  }

  const generateMarkerComponent = (id: number | string) => {
    return (
      <MarkerContainer id={id as string} className="marker-container">
        <Box
          className={`img-marker active-icon`}
          onClick={() => {
            handleMarkerClick(id)
          }}
          sx={{
            '& img': { cursor: 'pointer' },
          }}
        >
          <NextImage src={VariantImages.MapPin} quality={100} alt={''} />
        </Box>
      </MarkerContainer>
    )
  }

  const handleCardClick = (id: number | string) => {
    handleCardMouseEnter(id)
    setSelectedProfileId(id)
    setSelectedProfile(dataProfiles.filter((profile) => profile.id === id)[0])
    handleMarkerClick(id)
    setCoordinates(() => {
      const profileFiltered = dataProfiles.filter((profile) => profile.id === id)[0]
      return { lng: profileFiltered?.longitude, lat: profileFiltered?.latitude }
    })
  }

  const handleCardMouseEnter = (id: number | string) => {
    const htmlMarkers = document.getElementsByClassName('marker-container')

    Array.prototype.forEach.call(htmlMarkers, (marker) => {
      if (selectedProfileId !== id) {
        if (marker.id === id) {
          marker.classList.add('active')
        }
      }
    })
  }

  const handleCardMouseLeave = () => {
    const htmlMarkers = document.getElementsByClassName('marker-container')

    Array.prototype.forEach.call(htmlMarkers, (marker) => {
      if (marker.id !== selectedProfileId) marker.classList.remove('active')
    })
  }

  const handleMapDragEnd = (lng: number, lat: number) => {
    setCoordinates({ lng, lat })
  }

  const handleFiltersChange = (filters: SearchFiltersValues) => {
    if (dataProfiles.length && isFirstCall) {
      setIsFirstCall(false)
      return
    }
    setFilters(filters)
  }

  const handleMapZoom = (zoom: number) => {
    if (zoom >= 17.5) {
      setDistance(isMobile ? 0.07 : 0.1)
      return
    }
    if (zoom >= 16) {
      setDistance(isMobile ? 0.1 : 0.2)
      return
    }
    if (zoom >= 15) {
      setDistance(isMobile ? 0.2 : 0.3)
      return
    }
    if (zoom >= 14) {
      setDistance(isMobile ? 0.35 : 0.6)
      return
    }
    if (zoom >= 13.5) {
      setDistance(isMobile ? 0.4 : 1.3)
      return
    }
    if (zoom >= 12.5) {
      setDistance(isMobile ? 1.1 : 1.7)
      return
    }
    if (zoom >= 11) {
      setDistance(isMobile ? 2.5 : 5.5)
      return
    }
    if (zoom >= 9) {
      setDistance(isMobile ? 7 : 14)
      return
    }
    if (zoom >= 8) {
      setDistance(isMobile ? 60 : 100)
      return
    }

    setDistance(500)
    return
  }

  // 🔥 Fetch profiles
  const queryKeys = [`profiles_map_${type}`]
  const { isLoading, data, refetch, isRefetching } = useQuery(
    queryKeys,
    () => {
      if (!filters) return
      return searchProfilesAsync(isQueryOwner, {
        userAddress: settings?.address,
        filters: { ...filters, distance },
        type: SearchType.recommendation,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        itemsPerPage: props.itemsPerPage,
      }).catch((err) => console.log(err))
    },
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(filters),
      initialData: { member: [], nextPage: '', totalItems: 0 },
    },
  )

  const dataProfiles = data?.member || []

  const handleSearchAreaClick = () => {
    setIsFirstCall(false)
    setSelectedProfile(undefined)
    setSelectedProfileId(undefined)
    refetch()
  }

  // 📌 Màj des markers dès réception des profils
  useEffect(() => {
    if (!data) return
    if (!data.member.length) return
    setMarkers(
      data.member.map((profile) => ({
        id: profile.id,
        coordinates: [profile.longitude, profile.latitude],
        component: generateMarkerComponent(profile.id),
        isActive: false,
      })),
    )
  }, [data])

  // 🔥 Lancer la recherche à chaque changement de filtres
  useEffect(() => {
    if (!filters) return
    refetch()
  }, [filters])

  if (isMobile && floating) {
    return (
      <MapMobile
        homeMarker={homeMarker}
        markers={markers}
        coordinates={coordinates}
        profiles={dataProfiles as any}
        setSelectedProfileId={setSelectedProfileId}
        selectedProfileId={selectedProfileId}
        selectedProfile={selectedProfile}
        onClose={onClose}
        handleMapDragEnd={handleMapDragEnd}
        handleMapZoom={handleMapZoom}
        handleCardClick={handleCardClick}
        onSearchAreaClick={handleSearchAreaClick}
        isLoading={isLoading || isRefetching}
        onFiltersChange={handleFiltersChange}
        floating={floating}
      />
    )
  } else {
    return (
      <MapDesktop
        homeMarker={homeMarker}
        markers={markers}
        coordinates={coordinates}
        profiles={dataProfiles as any}
        setSelectedProfileId={setSelectedProfileId}
        selectedProfileId={selectedProfileId}
        onClose={onClose}
        handleMapDragEnd={handleMapDragEnd}
        handleMapZoom={handleMapZoom}
        handleCardMouseEnter={handleCardMouseEnter}
        handleCardMouseLeave={handleCardMouseLeave}
        onSearchAreaClick={handleSearchAreaClick}
        handleCardClick={handleCardClick}
        isLoading={isLoading || isRefetching}
        onFiltersChange={handleFiltersChange}
      />
    )
  }
}

export default Map
